import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import "./ams.css";

const Ctx = createContext({ me: null, lk: null, reload: () => {} });
export const useAms = () => useContext(Ctx);

export const AmsShell = ({ children }) => {
  const [me, setMe] = useState(null);
  const [lk, setLk] = useState(null);
  const reload = () => Promise.all([api.get("/ams/me"), api.get("/ams/lookups")]).then(([a, b]) => { setMe(a.data); setLk(b.data); }).catch(() => {});
  useEffect(() => { reload(); }, []);
  const can = (p) => !!me?.permissions?.includes(p);
  return <Ctx.Provider value={{ me, lk, can, reload }}><div className="ams" data-testid="ams-shell">{me && lk ? children : <div className="p-10 text-sm text-zinc-500">Loading…</div>}</div></Ctx.Provider>;
};

export const err = (e, fb = "Action failed") => toast.error(e?.response?.data?.detail || fb);
export const php = (n) => `₱${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const dt = (s) => (s ? new Date(s).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }) : "—");
export const label = (s) => String(s || "").replace(/_/g, " ");

const ST = { in_stock: "bg-zinc-100 text-zinc-700", assigned: "bg-blue-50 text-blue-700", in_repair: "bg-amber-50 text-amber-700", reserved: "bg-violet-50 text-violet-700",
  pending_return: "bg-orange-50 text-orange-700", lost: "bg-red-50 text-red-700", disposed: "bg-zinc-800 text-white", pending: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700", in_progress: "bg-blue-50 text-blue-700", completed: "bg-emerald-50 text-emerald-700",
  awaiting_approval: "bg-amber-50 text-amber-700", failed: "bg-red-50 text-red-700", critical: "bg-red-600 text-white", high: "bg-red-50 text-red-700",
  medium: "bg-amber-50 text-amber-700", low: "bg-zinc-100 text-zinc-600", requested: "bg-zinc-100 text-zinc-700", sanitising: "bg-amber-50 text-amber-700",
  sanitised: "bg-blue-50 text-blue-700", destroyed: "bg-zinc-800 text-white", redeployed: "bg-emerald-50 text-emerald-700", queued: "bg-red-50 text-red-700", executed: "bg-emerald-50 text-emerald-700" };
export const Badge = ({ v, testid }) => <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-medium capitalize whitespace-nowrap ${ST[v] || "bg-zinc-100 text-zinc-700"}`} data-testid={testid}>{label(v)}</span>;

export const Header = ({ kicker = "Asset Management", title, sub, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6 ams-rise">
    <div><div className="ams-kicker">{kicker}</div><h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1">{title}</h1>{sub && <p className="text-sm text-zinc-500 mt-1 max-w-2xl">{sub}</p>}</div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Stat = ({ k, v, hint, testid }) => (
  <div className="ams-card p-4 ams-rise"><div className="ams-kicker">{k}</div><div className="text-2xl font-semibold mt-1 tabular-nums" data-testid={testid}>{v}</div>{hint && <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>}</div>
);

export const Field = ({ l, children, className = "" }) => <label className={`block space-y-1 ${className}`}><span className="ams-kicker">{l}</span>{children}</label>;

export const Modal = ({ open, onClose, title, desc, children, wide }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className={`ams ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto rounded-sm`}>
      <DialogHeader><DialogTitle>{title}</DialogTitle>{desc && <DialogDescription>{desc}</DialogDescription>}</DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const Table = ({ cols, rows, empty = "No records yet.", testid, onRow }) => (
  <div className="ams-card overflow-x-auto">
    <table className="w-full ams-table" data-testid={testid}>
      <thead><tr>{cols.map((c) => <th key={c.k}>{c.l}</th>)}</tr></thead>
      <tbody>
        {!rows.length && <tr><td colSpan={cols.length} className="text-center text-zinc-400 py-10">{empty}</td></tr>}
        {rows.map((r, i) => <tr key={r.id || i} onClick={onRow ? () => onRow(r) : undefined} className={onRow ? "cursor-pointer" : ""} data-testid={`${testid}-row-${r.id || i}`}>{cols.map((c) => <td key={c.k}>{c.r ? c.r(r) : (r[c.k] ?? "—")}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

export const EmpSelect = ({ value, onChange, testid, placeholder = "Select employee" }) => {
  const { lk } = useAms();
  return <select className="ams-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}><option value="">{placeholder}</option>{lk.employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.employee_no}</option>)}</select>;
};

export const download = async (url, name) => {
  try { const r = await api.get(url, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); } catch (e) { err(e, "Download failed"); }
};

export const toCsv = (cols, rows, name) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([[cols.map(esc).join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n")], { type: "text/csv" }));
  a.download = name; a.click();
};

export const Denied = () => <div className="ams-card p-12 text-center text-sm text-zinc-500" data-testid="ams-denied">Your AMS role does not include this screen. Ask an AMS administrator in AMS Settings › User Management.</div>;
